/**
 * UserSearch — Search users from AD group, display in AG Grid.
 * Route: /administration/users
 *
 * Pattern: URL-driven search state (?q=...) with debounced input.
 * Clicking a user row navigates to /administration/users/:userId
 */

import { useMemo, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useLazyQuery } from '@apollo/client/react';
import Button from '@mui/material/Button';
import SearchIcon from '@mui/icons-material/Search';
import { graphql } from '../../gql';
import { DataGrid } from '../../components/data-grid';
import { TextInput, StatusChip, PageHeader, LoadingOverlay } from '../../components/ui';
import type { ColDef, RowClickedEvent } from 'ag-grid-community';

const SEARCH_USERS_QUERY = graphql(`
  query SearchUsersAdmin($query: String!) {
    searchUsers(query: $query) {
      id
      displayName
      mail
      userPrincipalName
      onPremisesSamAccountName
      onPremisesDomainName
      jobTitle
      department
      officeLocation
      accountEnabled
    }
  }
`);

interface UserRow {
  id: string;
  displayName: string | null | undefined;
  mail: string | null | undefined;
  onPremisesSamAccountName: string | null | undefined;
  onPremisesDomainName: string | null | undefined;
  userPrincipalName: string | null | undefined;
  jobTitle: string | null | undefined;
  department: string | null | undefined;
  officeLocation: string | null | undefined;
  accountEnabled: boolean | null | undefined;
}

export function UserSearchPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get('q') ?? '';
  const [searchTerm, setSearchTerm] = useState(initialQuery);

  const [searchUsers, { data, loading, error }] = useLazyQuery(SEARCH_USERS_QUERY);

  const handleSearch = () => {
    setSearchParams({ q: searchTerm });
    searchUsers({ variables: { query: searchTerm } });
  };

  const handleClear = () => {
    setSearchTerm('');
    setSearchParams({});
  };

  const users = data?.searchUsers ?? [];

  const columnDefs = useMemo<ColDef<UserRow>[]>(() => [
    { field: 'displayName', headerName: 'Name', flex: 1.5 },
    { field: 'onPremisesSamAccountName', headerName: 'Username', flex: 1 },
    { field: 'mail', headerName: 'Email', flex: 1.5 },
    { field: 'jobTitle', headerName: 'Job Title', flex: 1 },
    {
      field: 'accountEnabled',
      headerName: 'Status',
      width: 120,
      flex: 0,
      cellRenderer: (params: { value: boolean | null | undefined }) => {
        const enabled = params.value !== false;
        return <StatusChip active={enabled} />;
      },
    },
  ], []);

  const onRowClicked = useCallback((event: RowClickedEvent<UserRow>) => {
    const user = event.data;
    if (!user) return;

    const params = new URLSearchParams();
    params.set('sam', user.onPremisesSamAccountName ?? '');

    if (user.displayName) params.set('name', user.displayName);
    if (user.mail) params.set('email', user.mail);
    if (user.accountEnabled !== undefined && user.accountEnabled !== null) {
      params.set('enabled', String(user.accountEnabled));
    }

    navigate(`/administration/users/${user.id}?${params.toString()}`);
  }, [navigate]);

  return (
    <>
      <LoadingOverlay visible={loading} />

      <main className="max-w-[1440px] mx-auto px-6 py-8">
        <PageHeader title="User Management" />

        {/* Search bar */}
        <div className="flex items-center gap-3 mb-6">
          <TextInput
            search
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
            placeholder="Search by name, email, or UPN..."
          />
          <Button
            variant="contained"
            onClick={handleSearch}
            disabled={loading}
            startIcon={<SearchIcon />}
          >
            {loading ? 'Searching...' : 'Search'}
          </Button>
          <Button
            variant="outlined"
            onClick={handleClear}
          >
            Clear
          </Button>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 p-3 bg-negative-faded-20 border border-negative rounded text-sm text-negative">
            {error.message}
          </div>
        )}

        {/* AG Grid */}
        {users.length > 0 && (
          <DataGrid<UserRow>
            rowData={users as UserRow[]}
            columnDefs={columnDefs}
            onRowClicked={onRowClicked}
            height="80vh"
          />
        )}

        {/* Empty state */}
        {data && users.length === 0 && (
          <div className="text-center py-12 text-foreground-faded">
            No users found for &ldquo;{searchParams.get('q')}&rdquo;.
          </div>
        )}
      </main>
    </>
  );
}
